import Sketch from '@arcgis/core/widgets/Sketch.js';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer.js';
import type Geometry from '@arcgis/core/geometry/Geometry.js';
import { getMapView } from '@/map/mapStore';
import { commitSketch } from '@/map/sketches';
import {
  registerHandle,
  summarizeRef,
  type ResolvedRef,
} from '@/agent/refs';
import { onAgentAbort } from '@/agent/abort';
import type { Tool } from './types';

type Shape = 'polygon' | 'polyline' | 'point' | 'rectangle' | 'circle';
type Input = { shape: Shape };

export const sketchTool: Tool<Input> = {
  name: 'sketch',
  description:
    'Activate the Sketch widget so the user can draw a shape on the map with a live preview. Double-click finishes a polygon/polyline; rectangle/circle/point complete on release. The committed sketch stays on the map until cleared. Resolves with a handle to the drawn geometry.',
  input_schema: {
    type: 'object',
    properties: {
      shape: { enum: ['polygon', 'polyline', 'point', 'rectangle', 'circle'] },
    },
    required: ['shape'],
    additionalProperties: false,
  },
  execute: ({ shape }) =>
    new Promise<{ handle: string; summary: ReturnType<typeof summarizeRef> }>((resolve, reject) => {
      const view = getMapView();
      const map = view?.map;
      if (!view || !map) {
        reject(new Error('sketch: map not initialised'));
        return;
      }
      const tempLayer = new GraphicsLayer({ id: '__rcmc_sketch_temp', listMode: 'hide' });
      map.add(tempLayer);

      const widget = new Sketch({
        view,
        layer: tempLayer,
        creationMode: 'single',
        availableCreateTools: [shape],
        defaultUpdateOptions: { tool: 'reshape', enableScaling: true, enableRotation: true },
      });
      view.ui.add(widget, 'top-right');

      let settled = false;
      const cleanup = () => {
        createHandle.remove();
        document.removeEventListener('keydown', onKeyDown);
        offAbort();
        view.ui.remove(widget);
        widget.destroy();
        map.remove(tempLayer);
      };

      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key !== 'Escape' || settled) return;
        settled = true;
        cleanup();
        reject(new Error('sketch: user cancelled'));
      };
      document.addEventListener('keydown', onKeyDown);

      const offAbort = onAgentAbort(() => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error('sketch: aborted'));
      });

      const createHandle = widget.on('create', (event) => {
        if (settled) return;
        if (event.state === 'complete') {
          const geometry = event.graphic?.geometry as Geometry | undefined;
          if (!geometry) {
            settled = true;
            cleanup();
            reject(new Error('sketch: no geometry produced'));
            return;
          }
          commitSketch({ shape, geometry });

          const ref: ResolvedRef = {
            source_kind: 'geometry',
            geometry,
            extent: (geometry as { extent?: __esri.Extent }).extent ?? undefined,
          };
          const id = registerHandle(ref);
          settled = true;
          cleanup();
          resolve({ handle: id, summary: summarizeRef(ref) });
        } else if (event.state === 'cancel') {
          settled = true;
          cleanup();
          reject(new Error('sketch: user cancelled'));
        }
      });

      widget.create(shape);
    }),
};
